import { UsageByCoworld, type CoworldUsage } from "./usage-by-coworld";
import {
  Button,
  Card,
  EmptyState,
  Heading,
  SegmentedControl,
  Stack,
  Text,
} from "@softmax/design-system";
import { spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import {
  AccountReadStatus,
  type AccountReadState,
} from "../account-read-status";

export type AccountUsageData = {
  readonly total: string;
  readonly kinds: readonly {
    readonly id: string;
    readonly label: string;
    readonly amount: string;
    readonly detail: string;
  }[];
  readonly coworlds: readonly CoworldUsage[];
};

export type AccountUsagePeriod = "day" | "week" | "month" | "all";

export function AccountUsage({
  state,
  period,
  onPeriodChange,
  onRetry,
}: {
  readonly state: AccountReadState<AccountUsageData>;
  readonly period: AccountUsagePeriod;
  readonly onPeriodChange: (period: AccountUsagePeriod) => void;
  readonly onRetry: () => void;
}) {
  return (
    <Card>
      <Stack width="auto">
        <div {...stylex.props(styles.header)}>
          <Heading level={2}>Personal usage</Heading>
          <Button
            size="sm"
            onClick={onRetry}
            disabled={state.status === "loading"}
            aria-disabled={
              state.status === "ready" && state.refresh?.status === "refreshing"
            }
          >
            Refresh usage
          </Button>
        </div>
        <SegmentedControl
          aria-label="Usage period"
          value={period}
          onValueChange={onPeriodChange}
          items={[
            { value: "day", label: "Day" },
            { value: "week", label: "Week" },
            { value: "month", label: "Month" },
            { value: "all", label: "All" },
          ]}
        />
        {state.status !== "ready" ? (
          <AccountReadStatus state={state} onRetry={onRetry} />
        ) : (
          <>
            {state.refresh ? (
              <AccountReadStatus state={state.refresh} onRetry={onRetry} />
            ) : null}
            <Text as="p">Estimated total: {state.data.total}</Text>
            {state.data.kinds.length === 0 &&
            state.data.coworlds.length === 0 ? (
              <EmptyState title="No usage yet">
                Usage appears after your first request.
              </EmptyState>
            ) : (
              <>
                <div {...stylex.props(styles.rows)}>
                  {state.data.kinds.map((kind) => (
                    <div key={kind.id} {...stylex.props(styles.header)}>
                      <div>
                        <Text>{kind.label}</Text>
                        <Text as="p" size="small" tone="muted">
                          {kind.detail}
                        </Text>
                      </div>
                      <Text>{kind.amount}</Text>
                    </div>
                  ))}
                </div>
                {state.data.coworlds.length > 0 ? (
                  <UsageByCoworld rows={state.data.coworlds} />
                ) : null}
              </>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}

const styles = stylex.create({
  header: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: spacing.space16,
  },
  rows: { display: "grid", gap: spacing.space16 },
});
